import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Network, UserRound } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { FlowStep } from '@/features/home/data/homeContent'
import { cn } from '@/lib/utils'

const CHIP_CLASS: Record<FlowStep['kind'], string> = {
  request:
    'border-[var(--color-border)] bg-[var(--color-bg-tertiary)] font-mono text-[var(--color-text-secondary)]',
  reply:
    'border-[var(--color-border)] bg-[var(--color-bg-tertiary)] font-mono text-[var(--color-text-secondary)]',
  coordinator: 'border-primary/30 bg-primary/10 font-medium text-primary',
  specialist:
    'border-[var(--color-border-strong)] bg-[var(--color-bg-primary)] font-medium text-[var(--color-text-primary)]',
}

const CHIP_ICON: Partial<Record<FlowStep['kind'], LucideIcon>> = {
  coordinator: Network,
  specialist: UserRound,
}

const EASE = [0.16, 1, 0.3, 1] as const

interface FlowLaneProps {
  steps: Array<FlowStep>
  className?: string
}

/**
 * One path a request takes through a team, drawn as a chain of hops. The
 * shape of the chain is the point: an advising specialist bounces back
 * through the coordinator, a delegating one goes straight to the reply.
 * Hops enter left to right so the sequence reads as a sequence.
 */
export function FlowLane({ steps, className }: FlowLaneProps) {
  const reduce = useReducedMotion()

  return (
    <ol className={cn('flex flex-wrap items-center gap-y-2', className)}>
      {steps.map((step, i) => {
        const Icon = CHIP_ICON[step.kind]
        const delegate = step.via?.mode === 'delegate'
        return (
          <motion.li
            key={`${step.kind}-${i}`}
            className="flex items-center"
            initial={reduce ? false : { opacity: 0, x: -6 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.45, delay: i * 0.08, ease: EASE }}
          >
            {/* Label sits inline before the arrow (not stacked under it) so
                the connector is chip-height and stays on the chips' centre
                line, including when the hop wraps to a new row. */}
            {i > 0 && (
              <span className="flex items-center gap-1 px-1.5">
                {step.via && (
                  <span
                    className={cn(
                      'whitespace-nowrap font-mono text-[10px] leading-none',
                      delegate
                        ? 'text-[var(--color-info-text)]'
                        : 'text-[var(--color-text-tertiary)]',
                    )}
                  >
                    {step.via.label}
                  </span>
                )}
                <ArrowRight
                  aria-hidden
                  className={cn(
                    'size-3.5',
                    delegate
                      ? 'text-[var(--color-info)]'
                      : 'text-[var(--color-text-tertiary)]',
                  )}
                />
              </span>
            )}
            <span
              className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-caption transition-transform duration-300 ease-brisk hover:-translate-y-0.5',
                CHIP_CLASS[step.kind],
              )}
            >
              {Icon && <Icon aria-hidden className="size-3.5" strokeWidth={1.75} />}
              {step.label}
            </span>
          </motion.li>
        )
      })}
    </ol>
  )
}
